<template>
  <div
    class="absolute flex items-center gap-1 h-9 border border-n-800 rounded w-36 text-xs pl-1 top-[1px] overflow-hidden cursor-pointer"
    :class="{
      'text-g-500': !!reservation.tabId,
      'bg-n-900': isSelected,
      'bg-n-800': !isSelected
    }"
    :style="{
      left: `${positionX}rem`,
      width: `${durationWidth}rem`
    }"
  >
    <l-icon :name="statusIcon" />
    <div class="truncate">{{ reservation.name }} {{ reservation.surname }}</div>
    <l-status
      v-if="reservation.status === 'no-show'"
      size="small"
      type="secondary"
      :show-icon="false"
      status="error"
    >
      {{ t('reservations.no-show') }}
    </l-status>
    <l-status v-else-if="isLate" size="small" type="secondary" :show-icon="false" status="pending">
      {{ t('reservations.late') }}
    </l-status>
    <div class="ml-auto flex items-center gap-1 shrink-0 h-full">
      <div v-if="reservation.tables.length > 1" class="flex items-center gap-1 truncate">
        <l-icon name="combined" class="text-n-300" />
        <span class="truncate">{{ tables }}</span>
      </div>
      <div class="shrink-0 w-5 bg-n-700 h-full flex items-center justify-center text-n-300">
        {{ reservation.diners }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { differenceInMinutes } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { LIcon, LStatus } from '@last/core-ui/paprika'

import { useFloorplanStore } from '@/store/floorplan'
import type { Reservation } from '@/types'

import { LEFT_SHIFT, startOfWorkingHour } from './timeline.utils'

const props = defineProps<{
  reservation: Reservation
  tableId: string
  isSelected: boolean
  slotWidth: number
}>()

const { t } = useI18n()

const floorplanStore = useFloorplanStore()
const { tables: allTables } = storeToRefs(floorplanStore)

const positionX = computed(() => {
  const time = props.reservation.dateTime
  const minutes = differenceInMinutes(time, startOfWorkingHour(time))
  return (minutes / 30) * props.slotWidth + LEFT_SHIFT
})

const tables = computed(() => {
  if (props.reservation.tables && props.reservation.tables.length) {
    const otherTables = props.reservation.tables.filter(id => id !== props.tableId)
    const formattedTables = otherTables
      .slice(0, 3)
      .map(table => allTables.value[table].name)
      .join(', ')

    return `${formattedTables}${otherTables.length > 3 ? '...' : ''}`
  }
  return null
})

const durationWidth = computed(() => {
  const start = props.reservation.dateTime
  const end = props.reservation.endTime
  const minutes = differenceInMinutes(end, start)
  return (minutes / 30) * props.slotWidth
})

const statusIcon = computed(() => {
  if (props.reservation.tabId) {
    return 'calendar'
  }
  return 'time'
})

const isLate = computed(() => {
  const { dateTime } = props.reservation
  return dateTime && new Date(dateTime) < new Date()
})
</script>
